import { lazy } from "react";
import DashboardIcon from "@assets/SVGIcons/DashboardIcon.web";
import { NavigationGroup, NavigationRoute } from ".";
import GearIcon from "@assets/SVGIcons/GearIcon.web";
import UserIcon from "@assets/SVGIcons/UserIcon.web";
import OrganizationIcon from "@assets/SVGIcons/OrganizationIcon.web";
import { Navigate } from "react-router-dom";

const Dashboard = lazy(() => import("@components/pages/superAdmin/dashboard/Dashboard"));
const Organizations = lazy(() => import("@components/pages/superAdmin/organizations/Organizations"));
const OrganizationAppearance = lazy(() => import("@components/pages/superAdmin/organizations/OrganizationAppearance"));
const OrganizationAppearanceBranding = lazy(() => import("@components/pages/superAdmin/organizations/organization-appearance-tabs/Branding"));
const OrganizationAppearanceThemes = lazy(() => import("@components/pages/superAdmin/organizations/organization-appearance-tabs/AppearanceAndThemes"));
const OrganizationAppearanceProgressRangeColors = lazy(() => import("@components/pages/superAdmin/organizations/organization-appearance-tabs/ProgressRangeAndColors"));

const Setup = lazy(() => import("@components/pages/superAdmin/setup/Setup"));
const SetupSchoolYear = lazy(() => import("@components/pages/superAdmin/setup/setup-tabs/SetupSchoolYear"));
const SetupGrade = lazy(() => import("@components/pages/superAdmin/setup/setup-tabs/SetupGrade"));
const SetupSubject = lazy(() => import("@components/pages/superAdmin/setup/setup-tabs/SetupSubjects"));
const SetupCurriculums = lazy(() => import("@components/pages/superAdmin/setup/setup-tabs/SetupCurriculum"));
const SetupClassManagement = lazy(() => import("@components/pages/superAdmin/setup/setup-tabs/SetupClassManagement"));
const SetupParentTeacherAssignments = lazy(() => import("@components/pages/superAdmin/setup/setup-tabs/SetupParentTeacherAssignment"));
// Gradebook / grading configuration was removed from the super-admin (/root)
// level on 2026-06-23 — it is configured per-tenant under Admin → Setup →
// Gradebook. The super-admin save path was stale (pre-`subject_id`, wrote
// components to a dropped JSONB column) and never persisted components.
const SetupCalendars = lazy(() => import("@components/pages/superAdmin/setup/setup-tabs/SetupCalendars"));

const Users = lazy(() => import("@components/pages/superAdmin/users/Users"));

const Profile = lazy(() => import("@components/pages/superAdmin/profile/Profile"));
const EditProfile = lazy(() => import("@components/pages/superAdmin/profile/EditProfile"));
const ChangePassword = lazy(() => import("@components/pages/superAdmin/profile/ChangePassword"));

export const superAdminRoute: NavigationRoute[] = [
  {
    label: 'profile',
    component: <Profile />,
    path: 'profile'
  },
  {
    label: 'edit-profile',
    component: <EditProfile />,
    path: 'profile/edit'
  },
  {
    label: 'change-password',
    component: <ChangePassword />,
    path: 'profile/change-password'
  },
  {
    label: 'organization-appearance',
    component: <OrganizationAppearance />,
    path: 'organizations/:organization_id',
    children: [
      {
        index: true,
        component: <Navigate to="branding" replace />
      },
      {
        label: 'branding',
        path: 'branding',
        component: <OrganizationAppearanceBranding />,
      },
      {
        label: 'appearance-themes',
        path: 'appearance-themes',
        component: <OrganizationAppearanceThemes />,
      },
      {
        label: 'progress-range-colors',
        path: 'progress-range-colors',
        component: <OrganizationAppearanceProgressRangeColors />,
      },
    ]
  },

];

export const superAdminNavigation: NavigationGroup[] = [
  {
    title: "Main Navigation",
    routes: [
      {
        label: "dashboard",
        component: <Dashboard />,
        icon: <DashboardIcon fontSize="small" />,
        path: "dashboard",
      },
    ],
  },
  {
    title: "Administration",
    routes: [
      {
        label: "organizations",
        component: <Organizations />,
        icon: <OrganizationIcon fontSize="small" />,
        path: "organizations",
      },
      {
        label: "setup",
        component: <Setup />,
        icon: <GearIcon fontSize="small" />,
        path: "setup",
        children: [
          {
            index: true,
            component: <Navigate to="school-years" replace />
          },
          {
            label: "School Years",
            path: "school-years",
            component: <SetupSchoolYear />,
          },
          {
            label: "Grade Levels",
            path: "grade-levels",
            component: <SetupGrade />,
          },
          {
            label: "Subjects",
            path: "subjects",
            component: <SetupSubject />,
          },
          {
            label: "Curriculums",
            path: "curriculums",
            component: <SetupCurriculums />,
          },
          {
            label: "Class Management",
            path: "class-management",
            component: <SetupClassManagement />,
          },
          {
            label: "Parent-Teacher Assignments",
            path: "parent-teacher-assignments",
            component: <SetupParentTeacherAssignments />,
          },
          {
            label: 'Calendars',
            path: 'calendars',
            component: <SetupCalendars />
          }
        ]
      },
      {
        label: "users",
        component: <Users />,
        icon: <UserIcon fontSize="small" />,
        path: "users",
      },
    ],
  }
];

export default superAdminNavigation;
